import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import { useAIDocumentEditorContext } from '../../../../contexts/AIDocumentEditorContext';

import { useAIPromptCardStyles as useAICardStyles } from './styles';
import {
  IPromptCard,
  CardType,
  ITextBlock,
  IChecklist,
  IForm,
} from '@homeporter/types';
import { IDocument } from '@homeporter/types';
import { uuid } from '../../../../utils/helpers';
import { useCopilotContext } from '../../../../contexts/CopilotContext';
import { useBookingsContext } from '../../../../contexts/BookingsContext';

interface IChecklistListAlgorythm {
  name: string;
  min_word_count: number;
  temperature?: number;
}

const checklistAlgorythms: IChecklistListAlgorythm[] = [
  {
    name: 'Haiku Single Pass',
    min_word_count: 50,
    temperature: 70,
  },
  {
    name: 'Haiku Two Pass',
    min_word_count: 50,
    temperature: 70,
  },
  {
    name: 'Gemma Two Pass',
    min_word_count: 50,
    temperature: 70,
  },
  {
    name: 'Gemma-Encoder Two Pass',
    min_word_count: 50,
    temperature: 70,
  },
  {
    name: 'GPT Whole',
    min_word_count: 10000,
    temperature: 70,
  },
  {
    name: 'Sonnet Whole',
    min_word_count: 10000,
    temperature: 70,
  },
];

const formAlgorythms: IChecklistListAlgorythm[] = [
  {
    name: 'Sonnet Whole',
    min_word_count: 10000,
    temperature: 70,
  },
  {
    name: 'GPT Whole',
    min_word_count: 10000,
    temperature: 70,
  },
];

interface EditCardProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  editCardIndex: string;
  setEditCardIndex: (card: string) => void;
  showHidden: boolean;
  setShowHidden: (show: boolean) => void;
}

const EditCard = ({
  open,
  setOpen,
  editCardIndex,
  setEditCardIndex,
  showHidden,
  setShowHidden,
}: EditCardProps) => {
  const styles = useAICardStyles();
  const {
    checklistList,
    formList,
    documentLoading,
    viewDocument,
    setViewDocument,
  } = useCopilotContext();

  const {
    documentsRef,
    setSelectedCard,
    showGenerateReportPanel,
    setSaveEnabled,
    documentIndex,
    documentSection,
    setDocumentSection,
    defaultMessagesTemplate,
  } = useAIDocumentEditorContext();

  const { userSettings } = useBookingsContext();

  const generativeAIModels: string[] = userSettings?.dev
    ? [
        'sonnet-4',
        'sonnet-3.7',
        'sonnet-3.5',
        'gpt-4',
        'gpt-4o',
        'gpt-4o-mini',
        'haiku-3.5',
        'haiku-3',
        'gemma3:1b',
        'gemma3:4b',
        'mistral',
      ]
    : [
        'sonnet-4',
        'sonnet-3.7',
        'sonnet-3.5',
        'gpt-4',
        'gpt-4o',
        'gpt-4o-mini',
        'haiku-3.5',
        'haiku-3',
      ];

  const [editPrompt, setEditPrompt] = useState<string>('');
  const [context, setContext] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [structured, setStructured] = useState<boolean>(false);
  const [selectedModel, setSelectedModel] = useState<string>(
    generativeAIModels[0] || '',
  );

  const [selectedAlgorythm, setSelectedAlgorythm] = useState<number>(0);

  const [disableSave, setDisableSave] = useState<boolean>(true);
  const [selectedType, setSelectedType] = useState<CardType | null>(null);
  const [temperature, setTemperature] = useState<number>(70);

  const promptCardTypes: CardType[] =
    true || userSettings?.dev
      ? ['TextBlock', 'Prompt', 'Checklist', 'Form']
      : ['Prompt', 'Checklist', 'Form'];

  const promptCard = documentSection?.cards[
    Number(editCardIndex)
  ] as IPromptCard;

  const [messagesTemplate, setMessagesTemplate] = useState<string>(
    promptCard?.messagesTemplate || defaultMessagesTemplate,
  );

  useEffect(() => {
    if (editCardIndex === '') return;

    const index = Number(editCardIndex);
    const promptCard =
      index > -1 ? (documentSection?.cards[index] as IPromptCard) : null;

    console.log(
      '::::useEffect promptCard editCardIndex changed: ',
      promptCard,
      documentSection,
      editCardIndex,
    );

    console.log('setTitle: ', promptCard?.title);
    setTitle(promptCard?.title || '');
    setSelectedType(promptCard?.type || null);
    setContext(promptCard?.context || documentSection?.context || '');
    setEditPrompt(promptCard?.prompt || '');
    setStructured(promptCard?.structured || false);
  }, [editCardIndex]);

  useEffect(() => {
    setEditCardIndex(!documentSection?.cards?.length ? '-1' : '');
  }, [documentSection?.cards]);

  const appendNewCard = (auto: boolean = false) => {
    console.log('::::appendNewCard...');
    console.log('::::documentSection: ', documentSection);
    console.log('::::documentIndex: ', documentIndex);
    console.log('::::documentsRef?.current: ', documentsRef?.current);
    console.log('::::title: ', title);
    console.log();

    if (
      documentSection?.cards === undefined ||
      !documentsRef?.current?.[documentIndex]?.sections?.[
        documentSection.index
      ] === undefined
    ) {
      console.log(':::::documentSection exit...');
      alert('Unable to add new card.');
      return;
    }

    let newCard: IPromptCard | ITextBlock | IChecklist | IForm;
    if (selectedType === 'Prompt') {
      newCard = {
        id: uuid(),
        type: 'Prompt',
        model: selectedModel,
        temperature,
        prompt: editPrompt,
        messagesTemplate:
          messagesTemplate !== defaultMessagesTemplate ? messagesTemplate : '',
        context,
        response: '',
        modified: '',
        images: [],
        include: true,
        undo: [],
        redo: [],
        sort: 0,
        title,
        auto,
        structured,
      } as IPromptCard;
    } else if (selectedType === 'TextBlock') {
      newCard = {
        type: 'TextBlock',
        title,
        include: true,
        sort: 0,
        undo: [],
        redo: [],
        modified: '',
        response: 'Enter static text here...',
      } as ITextBlock;
    } else if (selectedType === 'Checklist') {
      if (!viewDocument) return;

      newCard = {
        type: 'Checklist',
        title,
        context,
        include: true,
        sort: 0,
        data: viewDocument.data,
        algorithm: checklistAlgorythms[selectedAlgorythm].name,
        temperature,
        min_word_count: checklistAlgorythms[selectedAlgorythm].min_word_count,
        name: viewDocument.title,
      } as IChecklist;
    } else if (selectedType === 'Form') {
      if (!viewDocument) {
        console.log(':::::viewDocument exit...');
        return;
      }

      newCard = {
        id: uuid(),
        type: 'Form',
        title,
        context,
        include: true,
        sort: 0,
        data: JSON.parse(JSON.stringify(viewDocument.data)),
        algorithm: formAlgorythms[selectedAlgorythm].name,
        temperature,
        min_word_count: formAlgorythms[selectedAlgorythm].min_word_count,
        name: viewDocument.title,
      } as IForm;
    } else {
      return;
    }

    const currentCard =
      editCardIndex !== '' && +editCardIndex >= 0
        ? documentSection.cards[+editCardIndex]
        : null;

    if (currentCard?.type === 'Prompt') {
      newCard.sort = currentCard.sort;
      newCard.title = currentCard.title;
    } else {
      const maxSortId = documentSection.cards.reduce((maxId, card) => {
        const currentId = card.sort;
        return currentId > maxId ? currentId : maxId;
      }, 0);

      newCard.sort = (maxSortId || 0) + 1;
    }

    console.log('::::newCard: ', JSON.stringify(newCard, null, 2));

    documentSection.cards.forEach((card) => {
      if (card.sort === newCard.sort) card.include = false;
    });

    documentSection.cards = [newCard, ...documentSection.cards];
    console.log('::::setDocument section 17...');
    setDocumentSection({
      ...documentSection,
    });

    console.log('::::documentRef: ', documentsRef.current);
    //need to be sure the changes to documentSection are reflected in documentsRef.current
    //if (documentsRef?.current) {
    //  documentsRef.current[documentIndex].sections[documentSection.index] =
    //    documentSection;
    //}

    setEditCardIndex('');
    setSaveEnabled(true);
    setSelectedCard({ index: 0, obj: newCard });
  };

  useEffect(() => {
    setDisableSave(
      !title ||
        (!showGenerateReportPanel && !context) ||
        (selectedType === 'Prompt' && editPrompt?.length < 6) ||
        ((selectedType === 'Checklist' || selectedType === 'Form') &&
          !viewDocument?.data),
    );
  }, [
    title,
    editPrompt?.length,
    selectedType,
    viewDocument?.did,
    viewDocument?.data,
    showGenerateReportPanel,
    context,
  ]);

  console.log('::::edit card formList: ', formList);

  return (
    <>
      {editCardIndex && (
        <Box sx={styles.cardOptionContainer}>
          {!!documentSection?.cards?.length && (
            <Box sx={styles.cardDetail}>
              <Typography variant="h4" sx={{ flex: 1, margin: 'auto 0' }}>
                {editCardIndex === '-1' ? 'Add' : 'Edit Prompt'} Card
              </Typography>
              <IconButton
                onClick={() => setEditCardIndex('')}
                sx={styles.closeButton}>
                <Close />
              </IconButton>
            </Box>
          )}

          {editCardIndex === '-1' && (
            <Box sx={styles.cardDetail}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  disabled={selectedType !== null && editCardIndex !== '-1'}
                  label={'Type'}
                  size="small"
                  value={selectedType || ''}
                  onClick={(e) => e.stopPropagation()}
                  onOpen={(e) => e.stopPropagation()}
                  MenuProps={{
                    container: document.body,
                    onClick: (e) => e.stopPropagation(),
                  }}
                  onChange={(e: any) => {
                    setViewDocument(null);
                    setSelectedType(e.target.value);
                  }}>
                  <MenuItem value="" disabled style={{ display: 'none' }}>
                    Select...
                  </MenuItem>
                  {promptCardTypes.map((type, index) => (
                    <MenuItem key={index} value={type}>
                      {type.toUpperCase()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {selectedType && (
            <Box sx={styles.cardDetail}>
              <TextField
                fullWidth
                variant="outlined"
                label="Title"
                placeholder="Enter title..."
                value={title}
                disabled={editCardIndex !== '-1'}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  console.log('::::onChange title: ', e.target.value);
                  setTitle(e.target.value);
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Box>
          )}

          {selectedType === 'Checklist' && (
            <>
              <Box sx={styles.cardDetail}>
                <FormControl fullWidth>
                  <InputLabel shrink={true}>Select the algorithm</InputLabel>
                  <Select
                    label={'Select the algorithm'}
                    size="small"
                    value={selectedAlgorythm}
                    onClick={(e) => e.stopPropagation()}
                    onOpen={(e) => e.stopPropagation()}
                    MenuProps={{
                      container: document.body,
                      onClick: (e) => e.stopPropagation(),
                    }}
                    onChange={(e: any) => {
                      setSelectedAlgorythm(Number(e.target.value));
                      setTemperature(
                        checklistAlgorythms[Number(e.target.value)]
                          .temperature || 70,
                      );
                    }}>
                    {checklistAlgorythms.map((algo, index) => (
                      <MenuItem key={index} value={index}>
                        {algo.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={styles.cardDetail}>
                <FormControl fullWidth>
                  <InputLabel shrink={true}>Checklist Type</InputLabel>
                  <Select
                    label="Checklist Type"
                    size="small"
                    value={viewDocument ?? ''}
                    onClick={(e) => e.stopPropagation()}
                    onOpen={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      const selected = checklistList.find(
                        (doc: IDocument) => doc.did === Number(e.target.value),
                      );
                      setViewDocument((selected && { ...selected }) || null);
                    }}
                    displayEmpty
                    renderValue={(selected) => (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {documentLoading ? (
                          <CircularProgress size={20} />
                        ) : null}
                        {typeof selected === 'object' && selected?.title
                          ? selected.title
                          : 'Select...'}
                      </Box>
                    )}>
                    <MenuItem value="" disabled>
                      Select...
                    </MenuItem>
                    {checklistList.map((item) => (
                      <MenuItem key={item.did} value={item.did}>
                        {item.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </>
          )}

          {selectedType === 'Form' && (
            <>
              <Box sx={styles.cardDetail}>
                <FormControl fullWidth>
                  <InputLabel shrink={true}>Select the algorithm</InputLabel>
                  <Select
                    label={'Select the algorithm'}
                    size="small"
                    value={selectedAlgorythm}
                    onClick={(e) => e.stopPropagation()}
                    onOpen={(e) => e.stopPropagation()}
                    MenuProps={{
                      container: document.body,
                      onClick: (e) => e.stopPropagation(),
                    }}
                    onChange={(e: any) => {
                      setSelectedAlgorythm(Number(e.target.value));
                      setTemperature(
                        formAlgorythms[Number(e.target.value)].temperature ||
                          70,
                      );
                    }}>
                    {formAlgorythms.map((algo, index) => (
                      <MenuItem key={index} value={index}>
                        {algo.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={styles.cardDetail}>
                <FormControl fullWidth>
                  <InputLabel shrink={true}>Form Type</InputLabel>
                  <Select
                    label="Form Type"
                    size="small"
                    value={viewDocument?.did ?? ''}
                    onClick={(e) => e.stopPropagation()}
                    onOpen={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      const selected = formList.find(
                        (doc: IDocument) => doc.did === Number(e.target.value),
                      );
                      setViewDocument(selected || null);
                    }}
                    displayEmpty
                    renderValue={(selectedDid) => {
                      const selectedDoc = formList.find(
                        (doc) => doc.did === selectedDid,
                      );
                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}>
                          {documentLoading ? (
                            <CircularProgress size={20} />
                          ) : null}
                          {selectedDoc?.title || 'Select...'}
                        </Box>
                      );
                    }}>
                    <MenuItem value="" disabled>
                      Select...
                    </MenuItem>
                    {formList.map((item) => (
                      <MenuItem key={item.did} value={item.did}>
                        {item.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </>
          )}

          {userSettings?.dev && selectedType !== null && (
            <Box sx={styles.cardDetail}>
              <FormControl fullWidth>
                <InputLabel
                  shrink={true}
                  sx={{ backgroundColor: 'white', px: 0.5 }}>
                  Temperature (0-100)
                </InputLabel>
                <TextField
                  value={temperature}
                  type="number"
                  size="small"
                  inputProps={{
                    min: 0,
                    max: 100,
                    step: 1,
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    const value = Math.min(
                      100,
                      Math.max(0, Number(e.target.value)),
                    );
                    setTemperature(value);
                  }}
                />
              </FormControl>
            </Box>
          )}
          {selectedType === 'Prompt' && (
            <>
              <Box sx={styles.cardDetail}>
                <FormControl fullWidth>
                  <InputLabel shrink={true}>Select the AI Model</InputLabel>
                  <Select
                    label={'Select the AI Model'}
                    size="small"
                    value={selectedModel}
                    onClick={(e) => e.stopPropagation()}
                    onOpen={(e) => e.stopPropagation()}
                    MenuProps={{
                      container: document.body,
                      onClick: (e) => e.stopPropagation(),
                    }}
                    onChange={(e: any) => {
                      setSelectedModel(e.target.value);
                    }}>
                    {generativeAIModels.map((model, index) => (
                      <MenuItem key={index} value={model}>
                        {model.toUpperCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={styles.cardDetail}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={structured}
                      onChange={(e) => {
                        setStructured(!structured);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  }
                  label="Include links to transcript"
                />
              </Box>
            </>
          )}

          {!showGenerateReportPanel &&
            selectedType &&
            selectedType != 'TextBlock' && (
              <Box sx={styles.cardDetail}>
                <TextField
                  sx={styles.promptInput}
                  fullWidth
                  multiline
                  rows={2}
                  variant="outlined"
                  label="Context"
                  placeholder="Enter context here..."
                  value={context}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setContext(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Box>
            )}

          {selectedType === 'Prompt' && (
            <Box sx={styles.cardDetail}>
              <TextField
                sx={styles.promptInput}
                fullWidth
                multiline
                rows={2}
                variant="outlined"
                label="Prompt"
                placeholder="Enter your prompt here..."
                value={editPrompt}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => setEditPrompt(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Box>
          )}

          <Box sx={styles.cardDetail}>
            <Button
              disabled={disableSave}
              sx={styles.generateButton}
              variant="contained"
              onClick={() => {
                showGenerateReportPanel
                  ? appendNewCard(false)
                  : appendNewCard(true);
              }}>
              {showGenerateReportPanel ||
              ['TextBlock', 'Checklist', 'Form'].includes(selectedType || '')
                ? 'Add'
                : 'Generate'}
            </Button>
          </Box>
        </Box>
      )}
    </>
  );
};

export default EditCard;
